
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Container } from '@/components/ui/container';
import StoreCard from '@/components/home/StoreCard';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, Store, ArrowLeft } from 'lucide-react';
import type { Profile } from '@/context/AuthContext';

const STORES_PER_PAGE = 12;

const Stores: React.FC = () => {
  const [stores, setStores] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalStores, setTotalStores] = useState(0);

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const term = searchParams.get('q') || '';
    setSearchTerm(term);
    setStores([]);
    setCurrentPage(0);
    setHasMore(true);
  }, [searchParams]);

  const fetchStores = useCallback(async (page: number, term: string) => {
    if (page === 0) setLoading(true); 
    else setLoading(false); 
    
    setError(null);

    try {
      const from = page * STORES_PER_PAGE;
      const to = from + STORES_PER_PAGE - 1;

      let query = supabase
        .from('profiles')
        .select('*', { count: 'exact' })
        .eq('is_store', true)
        .eq('is_blocked', false)
        .eq('store_approval_status', 'approved');

      if (term) {
        query = query.or(`store_name.ilike.%${term}%,username.ilike.%${term}%`);
      }

      query = query.order('created_at', { ascending: false }).range(from, to);

      const { data, error: supabaseError, count } = await query;

      if (supabaseError) {
        throw supabaseError;
      }

      // Data from Supabase should conform to the Profile type
      const typedStores = (data || []).map(store => ({
        ...store,
        id: store.id, // ensure id
        created_at: store.created_at, // ensure created_at is string
        role: store.role === 'admin' ? 'admin' : 'user',
      })) as Profile[];

      setStores(prevStores => page === 0 ? typedStores : [...prevStores, ...typedStores]);
      setTotalStores(count || 0);
      setHasMore((count || 0) > (page + 1) * STORES_PER_PAGE);

    } catch (err: any) {
      console.error('Error fetching stores:', err);
      setError('Pati një problem gjatë ngarkimit të dyqaneve.');
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStores(currentPage, searchTerm);
  }, [fetchStores, currentPage, searchTerm]);
  
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    if (event) event.preventDefault();
    setCurrentPage(0);
    setStores([]); 
    setHasMore(true); 
    setSearchParams(searchTerm ? { q: searchTerm } : {});
  };
  
  const loadMoreStores = () => {
    if (hasMore && !loading) {
      setCurrentPage(prevPage => prevPage + 1);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className="flex flex-col min-h-screen bg-Soft-Gray dark:bg-Dark-Purple">
      <Navbar />
      <Container className="py-8 flex-grow">
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
          <div className="flex items-center">
            <Button
                variant="outline"
                size="icon"
                onClick={handleGoBack}
                className="rounded-full mr-4 hidden md:inline-flex"
                aria-label="Kthehu Mbrapa"
              >
                <ArrowLeft size={16} />
            </Button>
            <div className="flex items-center">
              <Store size={32} className="mr-3 text-Primary-Purple" />
              <h1 className="text-3xl font-bold text-Charcoal-Gray dark:text-Pure-White">
                Të gjithë Dyqanet
              </h1>
            </div>
          </div>
          <form onSubmit={handleSearchSubmit} className="w-full md:w-auto md:max-w-md flex gap-2">
            <Input
              type="text"
              placeholder="Kërko dyqane..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="flex-grow"
              aria-label="Kërko dyqane"
            />
            <Button type="submit" variant="default" className="bg-Primary-Purple hover:bg-Secondary-Purple">
              <Search size={18} />
            </Button>
          </form>
        </div>

        {loading && stores.length === 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(STORES_PER_PAGE)].map((_, i) => (
              <div key={i} className="rounded-xl bg-white dark:bg-Charcoal-Gray p-4 shadow-md">
                <Skeleton className="h-32 w-full rounded-lg mb-4" />
                <div className="flex items-center space-x-3 mb-4">
                  <Skeleton className="h-16 w-16 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
                <Skeleton className="h-8 w-full" />
              </div>
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-10">
            <p className="text-red-500 text-lg">{error}</p>
            <Button onClick={() => fetchStores(0, searchTerm)} className="mt-4">
              Provo Përsëri
            </Button>
          </div>
        )}

        {!loading && !error && stores.length === 0 && (
          <div className="text-center py-10">
            <p className="text-Medium-Gray dark:text-Light-Gray text-lg">
              Nuk u gjetën dyqane {searchTerm ? `për "${searchTerm}"` : ''}.
            </p>
          </div>
        )}

        {stores.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {stores.map(store => (
              <StoreCard key={store.id} store={store} />
            ))}
          </div>
        )}

        {hasMore && !error && (
          <div className="text-center mt-10">
            <Button onClick={loadMoreStores} disabled={loading} size="lg">
              {loading && currentPage > 0 ? 'Duke ngarkuar...' : 'Ngarko më shumë'}
            </Button>
          </div>
        )}
         <div className="text-center mt-6 text-sm text-Medium-Gray dark:text-Light-Gray">
            {totalStores > 0 && `Duke shfaqur ${stores.length} nga ${totalStores} dyqane.`}
        </div>
      </Container>
      <Footer />
    </div>
  );
};

export default Stores;
